import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, Upload, FileText, Search, X, ChevronRight, Paperclip,
  Users as UsersIcon, Check, Download, RotateCcw, Pencil, Trash2,
  Folder, FolderOpen, Plane,
} from "lucide-react";
import { FOLDER_LABEL, UNFILED, foldersFor } from "@/lib/docFolders";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
// ISO (YYYY-MM-DD) <-> afișare DD/MM/YYYY
function isoToDisplay(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}
function displayToIso(txt) {
  const m = String(txt).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [_, dd, mm, yyyy] = m;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d) || d.getDate() !== +dd || d.getMonth() + 1 !== +mm) return null;
  return `${yyyy}-${mm}-${dd}`;
}
function DateInput({ value, onChange, className, style }) {
  const [text, setText] = React.useState(isoToDisplay(value));
  React.useEffect(() => { setText(isoToDisplay(value)); }, [value]);
  function handle(raw) {
    let digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
    if (out === "") onChange("");
    else { const iso = displayToIso(out); if (iso) onChange(iso); }
  }
  return (
    <input value={text} onChange={e => handle(e.target.value)} placeholder="ZZ/LL/AAAA"
      inputMode="numeric" className={className} style={style} />
  );
}
function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const AVATAR_COLORS = [
  { bg: "#EEF2F7", fg: "#3d68b1" }, { bg: "#F4EFE9", fg: "#8B6E4A" },
  { bg: "#EEF3EF", fg: "#3F6B4E" }, { bg: "#F5EEF0", fg: "#A0546C" },
  { bg: "#F0EEF5", fg: "#5B4E8A" }, { bg: "#F3F1EA", fg: "#7A6B34" },
];
function initials(n = "") { return n.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join(""); }
function colorFor(s = "") { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff; return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; }
function Avatar({ name, size = 40 }) {
  const c = colorFor(name || "?");
  return (
    <div className="flex items-center justify-center font-medium shrink-0 rounded-lg"
      style={{ width: size, height: size, background: c.bg, color: c.fg, fontSize: size * 0.35 }}>
      {initials(name) || "?"}
    </div>
  );
}


export default function PersonalDocsAdmin({ mode = "employees" }) {
  const isCollab = mode === "collaborators";
  const FOLDERS = foldersFor(mode);
  const noun = isCollab ? "colaborator" : "angajat";
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [receipts, setReceipts] = useState(null); // {doc, receipts}
  const [versionUploadFor, setVersionUploadFor] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [deleting, setDeleting] = useState(null); // doc pending delete
  const [openFolders, setOpenFolders] = useState({});
  const [leaveRecords, setLeaveRecords] = useState([]);

  const toggleFolder = (fid) => setOpenFolders(s => ({ ...s, [fid]: !s[fid] }));

  function renderLeaveRow(r) {
    const fmt = (iso) => { const d = new Date((iso || "") + "T00:00:00"); return isNaN(d) ? "-" : `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`; };
    return (
      <div key={r.id} className="flex items-center gap-4 py-3 pl-4 border-b border-neutral-100" data-testid={`leave-row-${r.id}`}>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 shrink-0">
          <Plane className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium text-ink truncate">{r.type?.name} — {fmt(r.start_date)} → {fmt(r.end_date)}</div>
          <div className="text-[12px] text-ink-muted">{r.business_days} zile lucrătoare · aprobat{r.hr_decision_at ? ` la ${fmt(r.hr_decision_at.slice(0,10))}` : ""}</div>
        </div>
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 shrink-0">Aprobată</span>
      </div>
    );
  }

  function renderDocRow(d) {
    return (
      <div key={d.id} className="flex items-center gap-4 py-3 pl-4 border-b border-neutral-100" data-testid={`doc-row-${d.id}`}>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="text-[14px] font-medium text-ink truncate">{d.title}</div>
            <span className="text-[10px] font-mono uppercase tracking-widest bg-neutral-100 text-ink-muted px-1.5 py-0.5 rounded">
              v{d.version}
            </span>
          </div>
          <div className="text-[12px] text-ink-muted truncate">
            {d.type_info?.name}
            {d.expiry_date && ` · Expiră ${fmtDate(d.expiry_date)}`}
            {d.requires_read_receipt && ` · ${d.receipts_count > 0 ? "Confirmat" : "Neconfirmat"}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {d.requires_read_receipt && (
            <button onClick={async () => {
              const { data } = await api.get(`/personal-docs/${d.id}/receipts`);
              setReceipts({ doc: d, receipts: data });
            }}
              className="text-[12px] text-ink-muted hover:text-ink px-2 py-1 rounded hover:bg-neutral-100">
              {d.receipts_count}/1 confirmat
            </button>
          )}
          <button onClick={() => setEditDoc(d)} data-testid={`doc-edit-${d.id}`}
            className="text-[12px] text-ink-muted hover:text-ink px-2 py-1 rounded hover:bg-neutral-100 inline-flex items-center gap-1">
            <Pencil className="h-3 w-3" /> Editează
          </button>
          <button onClick={() => setVersionUploadFor(d)}
            className="text-[12px] text-ink-muted hover:text-ink px-2 py-1 rounded hover:bg-neutral-100 inline-flex items-center gap-1">
            <RotateCcw className="h-3 w-3" /> Versiune nouă
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/personal-docs/${d.id}/download`}
            target="_blank" rel="noreferrer"
            className="text-ink-muted hover:text-ink p-1.5 rounded hover:bg-neutral-100">
            <Download className="h-4 w-4" />
          </a>
          <button onClick={() => setDeleting(d)} data-testid={`doc-delete-${d.id}`}
            title="Șterge documentul"
            className="text-ink-muted hover:text-red-600 p-1.5 rounded hover:bg-red-50">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  function renderFolders() {
    const byFolder = {};
    for (const d of docs) {
      const fid = d.folder && FOLDER_LABEL[d.folder] ? d.folder : UNFILED.id;
      (byFolder[fid] = byFolder[fid] || []).push(d);
    }
    const groups = FOLDERS.map(f => ({ id: f.id, label: f.label, docs: byFolder[f.id] || [] }));
    if ((byFolder[UNFILED.id] || []).length) {
      groups.push({ id: UNFILED.id, label: UNFILED.label, docs: byFolder[UNFILED.id] });
    }
    return groups.map(g => {
      const leaves = g.id === "concedii" ? leaveRecords : [];
      const count = g.docs.length + leaves.length;
      const isOpen = openFolders[g.id] ?? count > 0;
      return (
        <div key={g.id} className="border-b border-neutral-100 last:border-0">
          <button onClick={() => toggleFolder(g.id)} data-testid={`folder-${g.id}`}
            className="w-full flex items-center gap-3 py-3 text-left hover:bg-neutral-50 transition-colors">
            <ChevronRight className={`h-4 w-4 text-neutral-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            {isOpen ? <FolderOpen className="h-5 w-5 text-amber-500" /> : <Folder className="h-5 w-5 text-amber-500" />}
            <span className="text-[14px] font-medium text-ink flex-1">{g.label}</span>
            <span className="text-[11px] font-mono text-ink-muted">{count}</span>
          </button>
          {isOpen && (
            count === 0
              ? <div className="pl-12 pb-3 text-[12px] text-ink-muted/70 italic">— niciun document</div>
              : <div className="pb-1">
                  {g.docs.map(renderDocRow)}
                  {leaves.map(renderLeaveRow)}
                </div>
          )}
        </div>
      );
    });
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await api.delete(`/personal-docs/${deleting.id}`);
      toast.success("Document șters");
      setDeleting(null);
      loadDocs(selUser?.id);
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function loadUsers() {
    try {
      const [u, t] = await Promise.all([
        api.get("/staff/users"),
        api.get("/personal-docs/types"),
      ]);
      const list = u.data
        .filter(x => x.is_active !== false)
        .filter(x => isCollab ? x.role === "collaborator" : (x.role === "employee" || x.role === "admin"))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro", { sensitivity: "base" }));
      setUsers(list);
      setDocTypes(t.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }

  async function loadDocs(uid) {
    if (!uid) return;
    setLoadingDocs(true);
    try {
      const { data } = await api.get(`/personal-docs/user/${uid}`);
      setDocs(data);
      if (!isCollab) {
        try {
          const lr = await api.get("/timeoff/records", { params: { user_id: uid, status: "approved" } });
          setLeaveRecords(lr.data || []);
        } catch (_) { setLeaveRecords([]); }
      } else {
        setLeaveRecords([]);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoadingDocs(false); }
  }

  useEffect(() => {
    setSelUser(null);
    setDocs([]);
    setQ("");
    setLoading(true);
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  useEffect(() => { if (selUser) loadDocs(selUser.id); }, [selUser]);

  const filteredUsers = users.filter(u => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px] mx-auto">
        <header className="pb-10 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
            HR — Documente {isCollab ? "colaboratori" : "personale"}
          </div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            {isCollab ? "Documente colaboratori" : "Documente angajați"}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            {isCollab
              ? "Încarcă oferte de colaborare, CPS-uri, acte și alte documente pentru fiecare colaborator. Versionarea este urmărită automat."
              : "Încarcă contracte, adeverințe și alte documente pentru fiecare angajat. Versionarea și confirmările sunt urmărite automat."}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
          {/* Users list */}
          <div>
            <div className="mb-3 flex items-center gap-2 border-b border-neutral-200 pb-3">
              <Search className="h-4 w-4 text-ink-muted" />
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder={`Caută ${noun}`}
                className="flex-1 bg-transparent outline-none border-0 py-1 text-[14px] placeholder:text-ink-muted" />
            </div>
            <div className="max-h-[70vh] overflow-y-auto -mr-2 pr-2">
              {loading ? <Loader2 className="h-5 w-5 animate-spin text-ink-muted mx-auto my-8" /> :
                filteredUsers.map(u => (
                  <button key={u.id} onClick={() => setSelUser(u)}
                    className={`w-full flex items-center gap-3 py-3 px-2 rounded-lg text-left transition-colors ${
                      selUser?.id === u.id ? "bg-neutral-100" : "hover:bg-neutral-50"
                    }`}>
                    <Avatar name={u.name} size={36} />
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-ink truncate">{u.name}</div>
                      <div className="text-[12px] text-ink-muted truncate">{u.job_title || u.email}</div>
                    </div>
                  </button>
                ))
              }
            </div>
          </div>

          {/* Selected user docs */}
          <div>
            {!selUser ? (
              <div className="py-24 text-center text-ink-muted border-t border-neutral-200">
                <UsersIcon className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
                Selectează un {noun} pentru a-i gestiona documentele.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between pb-4 border-b border-neutral-200 mb-4">
                  <div>
                    <div className="text-[18px] font-medium text-ink">{selUser.name}</div>
                    <div className="text-[13px] text-ink-muted">{selUser.job_title || selUser.email}</div>
                  </div>
                  <Button variant="primary" size="md" icon={Upload}
                    onClick={() => setUploadOpen(true)}>
                    Încarcă document
                  </Button>
                </div>

                {loadingDocs ? (
                  <Loader2 className="h-5 w-5 animate-spin text-ink-muted mx-auto my-8" />
                ) : (
                  renderFolders()
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)}
        user={selUser} docTypes={docTypes} folders={FOLDERS}
        onDone={() => { setUploadOpen(false); loadDocs(selUser?.id); }} />

      <NewVersionDialog open={!!versionUploadFor} doc={versionUploadFor}
        onClose={() => setVersionUploadFor(null)}
        onDone={() => { setVersionUploadFor(null); loadDocs(selUser?.id); }} />

      <EditDialog open={!!editDoc} doc={editDoc} docTypes={docTypes} folders={FOLDERS}
        onClose={() => setEditDoc(null)}
        onDone={() => { setEditDoc(null); loadDocs(selUser?.id); }} />

      {deleting && (
        <div className="fixed inset-0 z-[60] bg-ink/30 flex items-center justify-center p-4" onClick={() => setDeleting(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-8" data-testid="doc-delete-dialog">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Ștergere document</div>
            <div className="text-[20px] font-display font-semibold text-ink mb-2">Ștergi „{deleting.title}"?</div>
            <p className="text-[14px] text-ink-muted mb-6">
              Documentul și toate versiunile lui vor fi șterse definitiv. Acțiunea nu poate fi anulată.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" size="md" onClick={() => setDeleting(null)}>Anulează</Button>
              <Button variant="danger-solid" size="md" icon={Trash2} onClick={confirmDelete}
                data-testid="doc-delete-confirm">
                Șterge definitiv
              </Button>
            </div>
          </div>
        </div>
      )}

      <ReceiptsDialog open={!!receipts} data={receipts}
        onClose={() => setReceipts(null)} />
    </PortalLayout>
  );
}


function UploadDialog({ open, onClose, user, docTypes, folders, onDone }) {
  const defaultFolder = (folders && folders[0]?.id) || "cim";
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    doc_type: "contract", folder: defaultFolder, title: "",
    document_number: "", issue_date: "", effective_date: "", expiry_date: "",
    issuer: "", requires_read_receipt: false, notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setForm({ doc_type: "contract", folder: defaultFolder, title: "", document_number: "", issue_date: "", effective_date: "", expiry_date: "", issuer: "", requires_read_receipt: false, notes: "" }); } }, [open, defaultFolder]);

  async function submit() {
    if (!file) { toast.error("Selectează un fișier"); return; }
    if (!form.title.trim()) { toast.error("Titlul e obligatoriu"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await api.post("/personal-docs/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document încărcat");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-ink/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
            Încarcă document pentru {user?.name}
          </div>
          <div className="text-[22px] font-display font-semibold text-ink mb-6">Document nou</div>
          <div className="space-y-4">
            <F label="Folder *">
              <select value={form.folder} onChange={e => setForm({ ...form, folder: e.target.value })}
                data-testid="upload-folder"
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }}>
                {folders.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </F>
            <F label="Tip *">
              <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })}
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }}>
                {docTypes.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
              </select>
            </F>
            <F label="Titlu *">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
            </F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Nr. document">
                <input value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Emitent">
                <input value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Data emiterii">
                <DateInput value={form.issue_date} onChange={v => setForm({ ...form, issue_date: v })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Data expirării">
                <DateInput value={form.expiry_date} onChange={v => setForm({ ...form, expiry_date: v })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
            </div>
            <F label="Fișier *">
              <label className="flex items-center gap-2 h-11 px-4 border border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-ink text-[13px] text-ink-muted">
                <Paperclip className="h-4 w-4" />
                <span>{file ? file.name : "Alege fișier (PDF/imagine/...)"}</span>
                <input type="file" className="hidden" onChange={e => setFile(e.target.files?.[0])} />
              </label>
            </F>
            <label className="flex items-center gap-2 text-[13px] text-ink">
              <input type="checkbox" checked={form.requires_read_receipt}
                onChange={e => setForm({ ...form, requires_read_receipt: e.target.checked })} />
              Necesită confirmare de citire de la angajat
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" size="md" onClick={onClose}>Anulează</Button>
            <Button variant="primary" size="md" icon={Upload} loading={busy} onClick={submit}>
              Încarcă
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function NewVersionDialog({ open, onClose, doc, onDone }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setNotes(""); } }, [open]);

  async function submit() {
    if (!file) { toast.error("Selectează fișierul"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", notes);
      await api.post(`/personal-docs/${doc.id}/version`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Versiune nouă publicată");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  if (!open || !doc) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-ink/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-8">
        <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
          Versiune nouă pentru
        </div>
        <div className="text-[20px] font-display font-semibold text-ink mb-1">{doc.title}</div>
        <div className="text-[13px] text-ink-muted mb-6">Va deveni versiunea v{doc.version + 1}</div>
        <F label="Fișier *">
          <label className="flex items-center gap-2 h-11 px-4 border border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-ink text-[13px] text-ink-muted">
            <Paperclip className="h-4 w-4" />
            <span>{file ? file.name : "Alege fișier"}</span>
            <input type="file" className="hidden" onChange={e => setFile(e.target.files?.[0])} />
          </label>
        </F>
        <div className="mt-4">
          <F label="Note versiune">
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
          </F>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" size="md" onClick={onClose}>Anulează</Button>
          <Button variant="primary" size="md" icon={Upload} loading={busy} onClick={submit}>
            Publică versiune
          </Button>
        </div>
      </div>
    </div>
  );
}


function EditDialog({ open, onClose, doc, docTypes, folders, onDone }) {
  const defaultFolder = (folders && folders[0]?.id) || "cim";
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && doc) {
      setFile(null);
      setForm({
        title: doc.title || "",
        folder: (doc.folder && folders.some(f => f.id === doc.folder)) ? doc.folder : defaultFolder,
        document_number: doc.document_number || "",
        issuer: doc.issuer || "",
        issue_date: doc.issue_date || "",
        effective_date: doc.effective_date || "",
        expiry_date: doc.expiry_date || "",
        requires_read_receipt: !!doc.requires_read_receipt,
        notes: doc.notes || "",
      });
    }
  }, [open, doc]);

  async function submit() {
    if (!form.title.trim()) { toast.error("Titlul e obligatoriu"); return; }
    setBusy(true);
    try {
      await api.put(`/personal-docs/${doc.id}`, {
        title: form.title,
        folder: form.folder,
        document_number: form.document_number,
        issuer: form.issuer,
        issue_date: form.issue_date || null,
        effective_date: form.effective_date || null,
        expiry_date: form.expiry_date || null,
        requires_read_receipt: form.requires_read_receipt,
        notes: form.notes,
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("notes", "Înlocuire fișier din editare");
        await api.post(`/personal-docs/${doc.id}/version`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(file ? "Document și fișier actualizate" : "Document actualizat");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  if (!open || !doc || !form) return null;
  const typeName = docTypes.find(t => t.code === doc.doc_type)?.name || doc.doc_type;
  return (
    <div className="fixed inset-0 z-[60] bg-ink/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="doc-edit-dialog">
        <div className="p-8">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
            Editare document · {typeName} · v{doc.version}
          </div>
          <div className="text-[22px] font-display font-semibold text-ink mb-6">Modifică documentul</div>
          <div className="space-y-4">
            <F label="Folder">
              <select value={form.folder} onChange={e => setForm({ ...form, folder: e.target.value })}
                data-testid="doc-edit-folder"
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }}>
                {folders.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </F>
            <F label="Titlu *">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="doc-edit-title"
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
            </F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Nr. document">
                <input value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Emitent">
                <input value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Data emiterii">
                <DateInput value={form.issue_date} onChange={v => setForm({ ...form, issue_date: v })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Data intrării în vigoare">
                <DateInput value={form.effective_date} onChange={v => setForm({ ...form, effective_date: v })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
              <F label="Data expirării">
                <DateInput value={form.expiry_date} onChange={v => setForm({ ...form, expiry_date: v })}
                  className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
              </F>
            </div>
            <F label="Note">
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
            </F>
            <F label="Înlocuiește fișierul (opțional — creează versiune nouă)">
              <label className="flex items-center gap-2 h-11 px-4 border border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-ink text-[13px] text-ink-muted">
                <Paperclip className="h-4 w-4" />
                <span>{file ? file.name : "Păstrează fișierul actual — sau alege altul"}</span>
                <input type="file" className="hidden" onChange={e => setFile(e.target.files?.[0])} data-testid="doc-edit-file" />
              </label>
            </F>
            <label className="flex items-center gap-2 text-[13px] text-ink">
              <input type="checkbox" checked={form.requires_read_receipt}
                onChange={e => setForm({ ...form, requires_read_receipt: e.target.checked })} />
              Necesită confirmare de citire de la angajat
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" size="md" onClick={onClose}>Anulează</Button>
            <Button variant="primary" size="md" icon={Check} loading={busy} onClick={submit} data-testid="doc-edit-save">
              Salvează
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ReceiptsDialog({ open, data, onClose }) {
  if (!open || !data) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-ink/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8">
        <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Confirmări citire</div>
        <div className="text-[20px] font-display font-semibold text-ink mb-6">{data.doc.title}</div>
        {data.receipts.length === 0 ? (
          <div className="text-[14px] text-ink-muted text-center py-12">
            Fără confirmări încă.
          </div>
        ) : (
          <div className="space-y-3">
            {data.receipts.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-neutral-100">
                <div>
                  <div className="text-[14px] font-medium text-ink">{r.user?.name}</div>
                  <div className="text-[12px] text-ink-muted">v{r.version} · IP {r.ip_address}</div>
                </div>
                <div className="text-[13px] text-ink-muted tabular-nums">
                  {`${fmtDate(r.read_at)}, ${new Date(r.read_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-6">
          <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
        </div>
      </div>
    </div>
  );
}


function F({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-display font-medium text-ink-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}
